import React, { useState, useEffect, useContext } from 'react';
import localization from 'assets/localization/localization';
import Layout from 'components/sales/layout/layout';
import { BaseUrl } from 'contexts/baseUrl/baseUrl';
import { Button } from 'react-bootstrap';
import { Link, useParams, useLocation } from 'react-router-dom';
import './ViewVendorDetail.css';
import { toast } from 'react-toastify';
import {
  formatBankTypeLabel,
  getApprovalBankDisplayType,
  getFinanceBankTypeDefault,
  isApprovedAddressRow,
  isApprovedBankRow,
  isNewBankPendingEditRequest,
  isPendingNewBankRow,
  isVendorCreationApproval,
  isVendorEditApprovalWithNewBank,
  shouldShowBankFinanceTypeSelector,
} from 'pages/vendorOnboarding/formData';
import {
  getVendorApprovalRequestLabel,
  getVendorApprovalResultMessage,
  isApprovedVendorStatus,
} from '../vendorSubmissionMessages';

const cell = (val) => (val != null && val !== '' ? val : 'N/A');

const getRequestTypeLabel = (vendor) => getVendorApprovalRequestLabel(vendor);

const mapRequestedAddressDetails = (requested, fallbackAddress = {}) => ({
  name: requested.name ?? fallbackAddress.name,
  address: requested.address ?? requested.vendor_address ?? fallbackAddress.address,
  address2: requested.address2 ?? requested.vendor_address2 ?? fallbackAddress.address2,
  country: requested.country ?? fallbackAddress.country,
  geo_state_id: requested.geo_state_id ?? requested.state_code ?? fallbackAddress.geo_state_id,
  geo_city_id: requested.geo_city_id ?? fallbackAddress.geo_city_id,
  geo_pincode_id: requested.geo_pincode_id ?? fallbackAddress.geo_pincode_id,
  geo_state: fallbackAddress.geo_state,
  geo_city: fallbackAddress.geo_city,
  geo_pincode: fallbackAddress.geo_pincode,
  contact: requested.contact ?? fallbackAddress.contact,
  phone_no: requested.phone_no ?? requested.contact_no ?? fallbackAddress.phone_no,
  email: requested.email ?? fallbackAddress.email,
  gst_no: requested.gst_no ?? fallbackAddress.gst_no,
  arn_no: requested.arn_no ?? fallbackAddress.arn_no,
  fax_no: requested.fax_no ?? fallbackAddress.fax_no,
  home_page: requested.home_page ?? fallbackAddress.home_page,
});

const mapRequestedBankDetails = (requested, fallbackBank = {}) => ({
  name: requested.bank_name ?? requested.name ?? fallbackBank.name,
  beneficiary_name: requested.beneficiary_name ?? fallbackBank.beneficiary_name,
  bank_account_no: requested.bank_account_no ?? fallbackBank.bank_account_no,
  ifsc_code: requested.ifsc_code ?? requested.ifsc_no ?? fallbackBank.ifsc_code,
  address: requested.bank_address ?? requested.address ?? fallbackBank.address,
  address2: requested.address2 ?? fallbackBank.address2,
  country: requested.country ?? fallbackBank.country,
  geo_state: fallbackBank.geo_state,
  geo_city: fallbackBank.geo_city,
  geo_pincode: fallbackBank.geo_pincode,
  contact: requested.contact ?? requested.contact_no ?? fallbackBank.contact,
  currency_code: requested.currency_code
    ? { code: requested.currency_code }
    : fallbackBank.currency_code,
  currency_code_id: requested.currency_code_id ?? fallbackBank.currency_code_id,
  swift_code: requested.swift_code ?? fallbackBank.swift_code,
  iban: requested.iban ?? fallbackBank.iban,
});

const pickRequestedAttachment = (requested, field) => {
  const fromDisplay = requested?.[field];
  if (typeof fromDisplay === 'string' && fromDisplay.trim() !== '') {
    return fromDisplay;
  }
  const fromUrl = requested?.[`${field}_url`];
  if (typeof fromUrl === 'string' && fromUrl.trim() !== '') {
    return fromUrl;
  }
  return null;
};

const mapRequestedVendorDetails = (vendor, requested) => ({
  ...vendor,
  name_of_firm: requested.name_of_firm ?? vendor.name_of_firm,
  contact_person_name: requested.contact_person_name ?? vendor.contact_person_name,
  contact_no: requested.contact_no ?? vendor.contact_no,
  email: requested.email ?? vendor.email,
  type_of_firm: requested.type_of_firm ?? vendor.type_of_firm,
  pan_no: requested.pan_no ?? vendor.pan_no,
  gst_no: requested.gst_no ?? vendor.gst_no,
  registration_type_of_gstin:
    requested.registration_type_of_gstin ?? vendor.registration_type_of_gstin,
  msme_reg_no: requested.msme_reg_no ?? vendor.msme_reg_no,
  pan_attachment:
    pickRequestedAttachment(requested, 'pan_attachment') ?? vendor.pan_attachment,
  gst_attachment:
    pickRequestedAttachment(requested, 'gst_attachment') ?? vendor.gst_attachment,
  cancel_cheque_attachment:
    pickRequestedAttachment(requested, 'cancel_cheque_attachment') ??
    vendor.cancel_cheque_attachment,
  msme_attatchment:
    pickRequestedAttachment(requested, 'msme_attatchment') ?? vendor.msme_attatchment,
  declaration_for_attatchment:
    pickRequestedAttachment(requested, 'declaration_for_attatchment') ??
    vendor.declaration_for_attatchment,
  pf_attatchement:
    pickRequestedAttachment(requested, 'pf_attatchement') ?? vendor.pf_attatchement,
  esic_attatchemnt:
    pickRequestedAttachment(requested, 'esic_attatchemnt') ?? vendor.esic_attatchemnt,
});

const ViewVendorDetail = ({ getVendorOnboardingList, vendorData, approveVendor }) => {
  const { baseUrl } = useContext(BaseUrl);
  const location = useLocation();
  const [vendor, setVendor] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [newBankType, setNewBankType] = useState('');

  const { id } = useParams();
  const isApprovalPendingView = location.pathname.includes('approval_pending_vendor_details');
  const backUrl = isApprovalPendingView
    ? `${baseUrl}/vendorOnboardedList?status=pending`
    : `${baseUrl}/vendorOnboardedList`;

  useEffect(() => {
    if (getVendorOnboardingList !== null) {
      (async () => {
        setIsLoading(true);
        await getVendorOnboardingList(null, 1, isApprovalPendingView, id);
        setIsLoading(false);
      })();
    }
  }, [id, isApprovalPendingView, getVendorOnboardingList, location.key]);

  useEffect(() => {
    if (vendorData?.data?.length > 0) {
      setVendor(vendorData.data[0]);
    }
  }, [vendorData]);

  const pendingEditRequest = vendor?.pending_edit_request;
  const requestedDetails = pendingEditRequest?.requested_details;
  const hasPendingRequestDetails =
    isApprovalPendingView && requestedDetails && Object.keys(requestedDetails).length > 0;
  const showBankTypeSelector = shouldShowBankFinanceTypeSelector(vendor);
  const isCreationApproval = isVendorCreationApproval(vendor);
  const isEditWithNewBank = isVendorEditApprovalWithNewBank(vendor);
  const bankFinanceApproval = pendingEditRequest?.bank_finance_approval;

  useEffect(() => {
    if (!showBankTypeSelector) {
      setNewBankType('');
      return;
    }

    setNewBankType(getFinanceBankTypeDefault(pendingEditRequest));
  }, [showBankTypeSelector, pendingEditRequest]);

  const isAddressPendingRequest =
    pendingEditRequest?.entity_type === 'address' ||
    pendingEditRequest?.action === 'add_address' ||
    pendingEditRequest?.action === 'form_update';

  const isBankPendingRequest =
    isEditWithNewBank ||
    pendingEditRequest?.entity_type === 'bank' ||
    isNewBankPendingEditRequest(pendingEditRequest);

  const getBankGeoFallback = () => {
    const banks = vendor?.vendor_bank_details || [];
    if (!banks.length) return vendor?.vendor_bank_detail || {};

    const stateId = requestedDetails?.geo_state_id;
    const cityId = requestedDetails?.geo_city_id;
    return (
      banks.find(
        (bank) =>
          String(bank.geo_state_id) === String(stateId) &&
          String(bank.geo_city_id) === String(cityId)
      ) || banks[0]
    );
  };

  const displayVendor =
    hasPendingRequestDetails &&
    (pendingEditRequest?.entity_type === 'vendor' ||
      pendingEditRequest?.action === 'form_update')
      ? mapRequestedVendorDetails(vendor, requestedDetails)
      : vendor;

  const getDefaultAddress = () => {
    const addresses = vendor?.vendor_addresses || [];
    return addresses[0] || vendor?.vendor_address_detail;
  };

  const getDefaultBank = () => {
    const banks = vendor?.vendor_bank_details || [];
    return banks[0] || vendor?.vendor_bank_detail;
  };

  const getApprovalPendingAddress = () => {
    if (hasPendingRequestDetails && isAddressPendingRequest) {
      return mapRequestedAddressDetails(
        requestedDetails,
        getDefaultAddress() || {}
      );
    }

    return getDefaultAddress();
  };

  const getApprovalPendingBank = () => {
    const banks = vendor?.vendor_bank_details || [];
    const pendingFromList = banks.find(isPendingNewBankRow);
    if (pendingFromList) return pendingFromList;

    if (hasPendingRequestDetails && isBankPendingRequest) {
      const bankRequest = requestedDetails?.new_bank ?? requestedDetails;
      return mapRequestedBankDetails(bankRequest, getBankGeoFallback());
    }

    if (isApprovalPendingView) {
      return getDefaultBank();
    }

    return vendor?.vendor_bank_detail;
  };

  const headerBtns = () => {
    if (isApprovalPendingView) {
      const requestTypeLabel = getRequestTypeLabel(vendor);
      return (
        <>
          <Button
            className="btn btn-primary"
            onClick={() => submitVendorData(id, 'APPROVE')}
          >
            {`Approve ${requestTypeLabel}`}
          </Button>
          <Button
            className="btn btn-primary"
            onClick={() => submitVendorData(id, 'REJECT')}
          >
            {`Reject ${requestTypeLabel}`}
          </Button>
        </>
      );
    }

    if (!isApprovedVendorStatus(vendor?.approval_status)) {
      return null;
    }

    return (
      <Link
        to={`${baseUrl}/add_vendor_address/${id}`}
        className="btn btn-primary vendor-detail-header-btn"
      >
        Add Address
      </Link>
    );
  };

  async function submitVendorData(vendor_id, status) {
    const requestData = { vendor_id, status };

    if (status === 'APPROVE' && showBankTypeSelector && newBankType) {
      requestData.new_bank_type = newBankType;
    }

    try {
      setIsLoading(true);
      const result = await approveVendor(requestData);

      if (result) {
        toast.success(getVendorApprovalResultMessage(status, vendor));
        setTimeout(() => {
          window.location.href = `${baseUrl}/vendorOnboardedList?status=pending`;
        }, 2000);
      } else {
        const actionLabel = getVendorApprovalRequestLabel(vendor).toLowerCase();
        toast.error(`Error while ${status === 'APPROVE' ? 'approving' : 'rejecting'} ${actionLabel}`);
      }
    } catch (error) {
      console.error('Failed to approve vendor:', error);
      const actionLabel = getVendorApprovalRequestLabel(vendor).toLowerCase();
      toast.error(`Failed to ${status === 'APPROVE' ? 'approve' : 'reject'} ${actionLabel}`);
    } finally {
      setIsLoading(false);
    }
  }

  const renderRow = (label, value) => (
    <div className="detail-item">
      <span className="label">{label}</span>
      <span className="value">{value ?? 'N/A'}</span>
    </div>
  );

  const renderGeoState = (item) =>
    cell(item?.geo_state?.state_name || item?.geo_state?.name || item?.state_name);

  const renderGeoCity = (item) =>
    cell(item?.geo_city?.city_name || item?.geo_city?.name || item?.city_name);

  const renderGeoPincode = (item) =>
    cell(item?.geo_pincode?.pincode || item?.pincode);

  const renderAddressBody = (address) => (
    <>
      {renderRow('Address Name', address.name)}
      {renderRow('Address Line 1', address.address)}
      {renderRow('Address Line 2', address.address2)}
      {renderRow('Country', address.country)}
      {renderRow('State', renderGeoState(address))}
      {renderRow('City', renderGeoCity(address))}
      {renderRow('Pincode', renderGeoPincode(address))}
      {renderRow('Contact', address.contact)}
      {renderRow('Phone No', address.phone_no)}
      {renderRow('Email', address.email)}
      {renderRow('GST No', address.gst_no)}
      {renderRow('ARN No', address.arn_no)}
      {renderRow('Fax No', address.fax_no)}
      {renderRow('Home Page', address.home_page)}
    </>
  );

  const renderBankTypeSelector = () => {
    if (!showBankTypeSelector) return null;

    const hasExistingPrimary = bankFinanceApproval?.has_existing_primary === true;

    return (
      <div className="vendor-bank-type-selector">
        <div className="vendor-bank-type-selector-title">Bank type for new account</div>
        <div className="vendor-bank-type-options">
          <label
            className={`vendor-bank-type-option${newBankType === 'OTHER' ? ' is-selected' : ''}`}
          >
            <input
              type="radio"
              className="vendor-bank-type-option-input"
              name="new_bank_type"
              value="OTHER"
              checked={newBankType === 'OTHER'}
              onChange={() => setNewBankType('OTHER')}
            />
            <div className="vendor-bank-type-option-content">
              <div className="vendor-bank-type-option-label">
                Other
                {hasExistingPrimary && (
                  <span className="vendor-bank-type-option-badge">Default</span>
                )}
              </div>
              <p className="vendor-bank-type-option-desc">
                Adds this account without changing the current Primary bank.
              </p>
            </div>
          </label>
          <label
            className={`vendor-bank-type-option${newBankType === 'PRIMARY' ? ' is-selected' : ''}`}
          >
            <input
              type="radio"
              className="vendor-bank-type-option-input"
              name="new_bank_type"
              value="PRIMARY"
              checked={newBankType === 'PRIMARY'}
              onChange={() => setNewBankType('PRIMARY')}
            />
            <div className="vendor-bank-type-option-content">
              <div className="vendor-bank-type-option-label">Primary</div>
              <p className="vendor-bank-type-option-desc">
                Makes this the Primary bank. The current Primary bank will become Other.
              </p>
            </div>
          </label>
        </div>
      </div>
    );
  };

  const renderBankBody = (bank, { displayType } = {}) => {
    const type = displayType ?? getApprovalBankDisplayType(vendor, bank);

    return (
      <>
        {renderRow('Bank Name', bank.name)}
        {type && renderRow('Bank Type', formatBankTypeLabel(type))}
      {renderRow('Beneficiary Name', bank.beneficiary_name)}
      {renderRow('Account No', bank.bank_account_no)}
      {renderRow('IFSC Code', bank.ifsc_code)}
      {renderRow(
        'Currency',
        bank.currency_code?.code ||
          bank.currencyCode?.code ||
          bank.currency_code_id
      )}
      {renderRow('Address Line 1', bank.address)}
      {renderRow('Address Line 2', bank.address2)}
      {renderRow('Country', bank.country)}
      {renderRow('State', renderGeoState(bank))}
      {renderRow('City', renderGeoCity(bank))}
      {renderRow('Pincode', renderGeoPincode(bank))}
      {renderRow('Contact', bank.contact)}
      {renderRow('SWIFT Code', bank.swift_code)}
      {renderRow('IBAN', bank.iban)}
      </>
    );
  };

  const renderAddressSection = () => {
    if (isApprovalPendingView) {
      const address = getApprovalPendingAddress();
      if (!address) return null;

      return (
        <div className="details-card">
          <div className="details-card-header">
            {hasPendingRequestDetails && isAddressPendingRequest
              ? 'Requested Address Details'
              : 'Address Details'}
          </div>
          <div className="details-card-body">{renderAddressBody(address)}</div>
        </div>
      );
    }

    const addresses = (vendor?.vendor_addresses || []).filter(isApprovedAddressRow);
    if (!addresses.length) return null;

    return addresses.map((address, index) => (
      <div className="details-card" key={address.id || `address-${index}`}>
        <div className="details-card-header details-card-header--with-action">
          <span>
            Address{address.name ? ` — ${address.name}` : ''}
            {address.is_legacy ? ' (Legacy)' : ''}
          </span>
          {address.id && !address.is_legacy && (
            <Link
              to={`${baseUrl}/edit_vendor_address/${id}/${address.id}`}
              className="details-card-header-link"
            >
              Edit Address
            </Link>
          )}
        </div>
        <div className="details-card-body">{renderAddressBody(address)}</div>
      </div>
    ));
  };

  const renderBankSection = () => {
    if (isApprovalPendingView) {
      const banks = vendor?.vendor_bank_details || [];

      if (banks.length > 0) {
        return banks.map((bank, index) => {
          const isPending = isPendingNewBankRow(bank);
          const displayType = getApprovalBankDisplayType(vendor, bank);
          const typeLabel = displayType ? formatBankTypeLabel(displayType) : '';

          return (
            <div className="details-card" key={bank.id ?? `bank-${index}`}>
              <div className="details-card-header">
                <span>
                  {isPending && !isCreationApproval
                    ? 'Requested Bank Details'
                    : `Bank Details${bank.name ? ` — ${bank.name}` : ''}`}
                  {typeLabel ? ` — ${typeLabel}` : ''}
                  {isPending && !isCreationApproval ? ' — Pending finance approval' : ''}
                  {!isPending && bank.is_legacy ? ' (Legacy)' : ''}
                </span>
              </div>
              <div className="details-card-body">
                {renderBankBody(bank, { displayType })}
                {isPending && showBankTypeSelector && renderBankTypeSelector()}
              </div>
            </div>
          );
        });
      }

      if (isCreationApproval) {
        const bank = getApprovalPendingBank() || getDefaultBank();
        if (!bank) return null;

        return (
          <div className="details-card">
            <div className="details-card-header">Bank Details — Primary</div>
            <div className="details-card-body">
              {renderBankBody(bank, { displayType: 'PRIMARY' })}
            </div>
          </div>
        );
      }

      if (!isEditWithNewBank && !isBankPendingRequest) {
        return null;
      }

      const bank = getApprovalPendingBank();
      if (!bank) return null;

      return (
        <div className="details-card">
          <div className="details-card-header">Requested Bank Details</div>
          <div className="details-card-body">
            {renderBankBody(bank)}
            {showBankTypeSelector && renderBankTypeSelector()}
          </div>
        </div>
      );
    }

    const banks = (vendor?.vendor_bank_details || []).filter(isApprovedBankRow);
    if (!banks.length) return null;

    return banks.map((bank, index) => {
      const typeLabel = bank.type ? formatBankTypeLabel(bank.type) : '';

      return (
        <div className="details-card" key={bank.id || `bank-${index}`}>
          <div className="details-card-header">
            Bank Details{bank.name ? ` — ${bank.name}` : ''}
            {bank.is_legacy ? ' (Legacy)' : ''}
            {typeLabel ? ` — ${typeLabel}` : ''}
          </div>
          <div className="details-card-body">{renderBankBody(bank)}</div>
        </div>
      );
    });
  };

  return (
    <Layout
      title={localization.BacktoList}
      isLoading={isLoading}
      isGoback
      linkFlag
      linkUrl={backUrl}
      Buttons={headerBtns}
      useNewHeaderLayout
      className="vendor-detail-page"
    >
      {vendor && (
        <div className="vendor-details-wrapper">
          <div className="details-card">
            <div className="details-card-header">
              {hasPendingRequestDetails && pendingEditRequest?.action === 'form_update'
                ? 'Requested Vendor Details'
                : 'Vendor Details'}
            </div>
            <div className="details-card-body">
              {renderRow('Vendor ID', displayVendor.id)}
              {renderRow('Vendor Code', displayVendor.vendor_code)}
              {renderRow('Firm Name', displayVendor.name_of_firm)}
              {renderRow('Contact Person', displayVendor.contact_person_name)}
              {renderRow('Contact No', displayVendor.contact_no)}
              {renderRow('Email', displayVendor.email)}
              {renderRow('Type of Firm', displayVendor.type_of_firm)}
              {renderRow('PAN No', displayVendor.pan_no)}
              {renderRow(
                'Registration Type of GSTIN',
                displayVendor.registration_type_of_gstin
              )}
              {renderRow('MSME Registered', displayVendor.msme_reg_no)}
              {renderRow('Approval Status', displayVendor.approvalStatus)}
              {renderRow('Created By', displayVendor.userName)}
              {/* {renderRow('Status', displayVendor.status === '1' ? 'Active' : 'Inactive')} */}
              {isApprovalPendingView &&
                pendingEditRequest?.remarks &&
                renderRow('Remarks', pendingEditRequest.remarks)}
            </div>
          </div>

          {renderAddressSection()}
          {renderBankSection()}

          <div className="details-card">
            <div className="details-card-header">Documents</div>
            <div className="details-card-body">
              {renderRow(
                'PAN Attachment',
                displayVendor.pan_attachment ? (
                  <a href={displayVendor.pan_attachment} target="_blank" rel="noreferrer">
                    View
                  </a>
                ) : (
                  'Not Uploaded'
                )
              )}
              {renderRow(
                'GST Attachment',
                displayVendor.gst_attachment ? (
                  <a href={displayVendor.gst_attachment} target="_blank" rel="noreferrer">
                    View
                  </a>
                ) : (
                  'Not Uploaded'
                )
              )}
              {renderRow(
                'Cancelled Cheque Attachment',
                displayVendor.cancel_cheque_attachment ? (
                  <a href={displayVendor.cancel_cheque_attachment} target="_blank" rel="noreferrer">
                    View
                  </a>
                ) : (
                  'Not Uploaded'
                )
              )}
              {renderRow(
                'MSME Attachment',
                displayVendor.msme_attatchment ? (
                  <a href={displayVendor.msme_attatchment} target="_blank" rel="noreferrer">
                    View
                  </a>
                ) : (
                  'Not Uploaded'
                )
              )}
              {renderRow(
                'Declaration For Attachment',
                displayVendor.declaration_for_attatchment ? (
                  <a href={displayVendor.declaration_for_attatchment} target="_blank" rel="noreferrer">
                    View
                  </a>
                ) : (
                  'Not Uploaded'
                )
              )}
              {renderRow(
                'PF Attachment',
                displayVendor.pf_attatchement ? (
                  <a href={displayVendor.pf_attatchement} target="_blank" rel="noreferrer">
                    View
                  </a>
                ) : (
                  'Not Uploaded'
                )
              )}
              {renderRow(
                'ESIC Attachment',
                displayVendor.esic_attatchemnt ? (
                  <a href={displayVendor.esic_attatchemnt} target="_blank" rel="noreferrer">
                    View
                  </a>
                ) : (
                  'Not Uploaded'
                )
              )}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default ViewVendorDetail;
